// src/lib/therapistApi.js - Comprehensive API service for therapist portal
import http from "./http";

/* ==================== THERAPIST PROFILE ==================== */

export const fetchTherapistProfile = (therapistId) =>
  http.get(`/therapists/${therapistId}`).then((res) => res.data);

export const updateTherapistProfile = (therapistId, payload) =>
  http.put(`/therapists/${therapistId}`, payload).then((res) => res.data);

export const fetchCountsBySetting = () =>
  http.get("/therapists/counts-by-setting").then((res) => res.data);

/* ==================== THERAPIST PORTAL (APPOINTMENTS & CLIENTS) ==================== */

export const fetchAppointments = (therapistId, params) =>
  http.get(`/therapist-portal/${therapistId}/appointments`, { params }).then((res) => res.data);

export const fetchClients = (therapistId, params) =>
  http.get(`/therapist-portal/${therapistId}/clients`, { params }).then((res) => res.data);

/* ==================== SOAP NOTES ==================== */

export const fetchSoapNotes = (therapistId) =>
  http.get(`/therapist-portal/${therapistId}/soap`).then((res) => res.data);

export const createSoapNote = (therapistId, payload) =>
  http.post(`/therapist-portal/${therapistId}/soap`, payload).then((res) => res.data);

export const updateSoapNote = (therapistId, noteId, payload) =>
  http.patch(`/therapist-portal/${therapistId}/soap/${noteId}`, payload).then((res) => res.data);

export const signSoapNote = (therapistId, noteId) =>
  http.post(`/therapist-portal/${therapistId}/soap/${noteId}/sign`).then((res) => res.data);

/* ==================== THERAPIST PERSONA ==================== */

// PATCH /therapists/:id/persona  (BookingFlow-aligned persona save)
export const upsertTherapistPersona = (therapistId, payload) =>
  http.patch(`/therapists/${therapistId}/persona`, payload).then((res) => res.data);

// POST /therapists/:id/persona/ai-from-bio  (draft persona from bio)
export const aiPersonaFromBio = (therapistId, bio) =>
  http.post(`/therapists/${therapistId}/persona/ai-from-bio`, { bio }).then((res) => res.data);

// (kept) This is for client-intake AI, different feature:
export const createPersonaViaAI = ({ email, text }) =>
  http.post("/persona/ai", { email, text }).then((res) => res.data);

/* ==================== BOOKINGS - CLINIC SIDE ==================== */

// Create booking from clinic side
export const createClinicBooking = (payload) =>
  http.post("/bookings/clinic", payload).then((res) => res.data);

// Update clinic side booking
export const updateClinicBooking = (bookingId, payload) =>
  http.put(`/bookings/clinic/${bookingId}`, payload).then((res) => res.data);

// Delete clinic side booking
export const deleteClinicBooking = (bookingId) =>
  http.delete(`/bookings/clinic/${bookingId}`).then((res) => res.data);

// Get booking details (for debugging)
export const getBookingDetails = (bookingId) =>
  http.get(`/bookings/${bookingId}/details`).then((res) => res.data);

/* ==================== CLIENT CHECK-INS ==================== */

// Get pending check-ins (clinic staff)
export const getPendingCheckIns = () =>
  http.get("/bookings/checkins/pending").then((res) => res.data);

// Get confirmed check-ins (clinic staff)
export const getConfirmedCheckIns = () =>
  http.get("/bookings/checkins/confirmed").then((res) => res.data);

// Confirm a check-in (clinic staff)
export const confirmCheckIn = (bookingId) =>
  http.put(`/bookings/checkins/${bookingId}/confirm`).then((res) => res.data);

// Cancel a pending check-in (clinic staff)
export const cancelPendingCheckIn = (bookingId) =>
  http.put(`/bookings/checkins/${bookingId}/cancel`).then((res) => res.data);

// Client check-in (public - no auth required, but included for completeness)
export const clientCheckIn = (payload) =>
  http.post("/bookings/checkin", payload).then((res) => res.data);

/* ==================== THERAPIST AVAILABILITY / AWAYS ==================== */

// Create therapist away period
export const createTherapistAway = (payload) =>
  http.post("/therapists/aways", payload).then((res) => res.data);

// Get all therapist aways for clinic
export const getTherapistAways = () =>
  http.get("/therapists/aways").then((res) => res.data);

// Update therapist away period
export const updateTherapistAway = (awayId, payload) =>
  http.put(`/therapists/aways/${awayId}`, payload).then((res) => res.data);

// Delete therapist away period
export const deleteTherapistAway = (awayId, payload) =>
  http.delete(`/therapists/aways/${awayId}`, { data: payload }).then((res) => res.data);

export const fetchAvailabilitySlots = (setting, dateISO) =>
  http.get("/availability/slots", { params: { setting, date: dateISO } }).then((res) => res.data);

/* ==================== REVIEWS ==================== */

// Add review for therapist (requires client auth)
export const addTherapistReview = (therapistId, payload) =>
  http.post(`/therapists/${therapistId}/reviews`, payload).then((res) => res.data);

// Get therapist reviews
export const getTherapistReviews = (therapistId) =>
  http.get(`/therapists/${therapistId}/reviews`).then((res) => res.data);

/* ==================== BOOKING HOLDS (CLIENT FLOW) ==================== */

// Create a temporary hold
export const createBookingHold = (payload) =>
  http.post("/bookings/hold", payload).then((res) => res.data);

// Confirm a hold to create booking
export const confirmBookingHold = (holdId) =>
  http.post("/bookings/confirm", { holdId }).then((res) => res.data);

/* ==================== AI SERVICES ==================== */

// Improve SOAP note text with AI
export const improveSoapText = (text, section) =>
  http.post("/ai/improve-soap-text", { text, section }).then((res) => res.data);

/* ==================== CLIENT INTAKE FORMS ==================== */

// Get client intake form by bookingId, clientEmail, or clientId
export const getIntakeForm = (params) => {
  const queryParams = new URLSearchParams();
  if (params.id) queryParams.append("id", params.id);
  if (params.bookingId) queryParams.append("bookingId", params.bookingId);
  if (params.email) queryParams.append("email", params.email);
  if (params.clientId) queryParams.append("clientId", params.clientId);
  
  return http
    .get(`/client/intake-form?${queryParams.toString()}`)
    .then((res) => res.data);
};